import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import type {FinancialProductQuoteEnvelope} from '../types/FinancialProductQuoteEnvelope';
import type {FinancialProductQuoteOffer} from '../types/FinancialProductQuoteOffer';
import {getCurrencyScale} from './getCurrencyScale';
import {isNonNegativeDecimal} from './isNonNegativeDecimal';
import {QuoteDecimal} from './QuoteDecimal';

const hasPositivePrice = (envelope: FinancialProductQuoteEnvelope) => {
  const price = envelope.vehiclePriceWithVat;

  return (
    isNotNil(price) &&
    isNotNil(getCurrencyScale(price.amount)) &&
    !new QuoteDecimal(price.amount).isZero()
  );
};

/**
 * Whether the published inputs can price any coordinate of this offer exactly as the backend
 * would: a LIVE body (a contract snapshot is never priced locally) whose offer carries its rates.
 * Financing needs a positive vehicle price, the processing fee in the same currency, and a
 * nominal rate and APR on every cell; leasing needs the leasing down payment and both monthly
 * rates on every cell. Absent members (an older backend) mean the same as withheld (null).
 */
export function canComputeFinancialProductQuoteLocally(
  envelope: FinancialProductQuoteEnvelope,
  offer: FinancialProductQuoteOffer
): boolean {
  if (envelope.mode !== 'LIVE') {
    return false;
  }

  return match(offer.productType)
    .with('LEASING', () => {
      const cells = offer.availableLeasingCells;

      return (
        isNotNil(offer.leasingDownPayment) &&
        cells.length > 0 &&
        cells.every((cell) => isNotNil(cell.monthlyRate) && isNotNil(cell.monthlyRateNet))
      );
    })
    .otherwise(() => {
      const cells = offer.availableFinancingCells;
      const fee = offer.processingFee;

      return (
        hasPositivePrice(envelope) &&
        isNotNil(fee) &&
        fee.currency === envelope.vehiclePriceWithVat?.currency &&
        isNonNegativeDecimal(fee.amount) &&
        isNotNil(cells) &&
        cells.length > 0 &&
        cells.every(
          (cell) =>
            isNotNil(cell.nominalRate) &&
            isNonNegativeDecimal(cell.nominalRate) &&
            isNotNil(cell.apr)
        )
      );
    });
}
